import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { RoleFormRule, RoleFormSchema } from "@core/form/form.rule";
import { useFormDefaultRole } from "@core/form/form-default-role.hook";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { ActionEnum } from "@core/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { 
  useQyUpdateRole, 
  useQyGetRoleById, 
  useQyDeleteRole 
} from "@core/query/role.query";
import { GetResponseRoleModel } from "@core/model/query-model-role";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";

export function useEditRole() {
  // LOCAL DECLARATION
  const { getDefault } = useFormDefaultRole();
  const navigate = useNavigate();
  const { mapUpdateRole, mapDeleteRole } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { roleId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Role
  const handleGetApiSuccess = (data: GetResponseRoleModel) => {
    if (data && data.count && data.count > 0) {
      const responseData = data.data?.[0];
      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
    }
  };
  const {
    data: roleResponse,
    isLoading,
    isError: roleError,
  } = useQyGetRoleById(roleId || "", handleGetApiSuccess);
  const roleData = roleResponse?.data?.[0];

  // API UPDATE Role
  const handleUpdateApiSuccess = () => {
    showSuccess("Role updated successfully");
  };
  const { mutate: updateRole, isLoading: isEditLoading } = useQyUpdateRole(
    handleUpdateApiSuccess
  );

  // API DELETE Role
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Role deleted successfully");
  };
  const { mutate: deleteRole } = useQyDeleteRole(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<RoleFormSchema>({
    defaultValues: getDefault(roleData),
    resolver: zodResolver(RoleFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: RoleFormSchema) => {
    if (!roleData) {
      throw new Error("No role data");
    }

    const formData = mapUpdateRole(form, roleData.code);
    updateRole(formData);
  };
  const handleValidateError = (err: FieldErrors<RoleFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!roleData) {
      throw new Error("No role data found");
    }

    const form = mapDeleteRole(roleData.code);
    deleteRole(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    roleId,
    roleData,
    isLoading,
    roleError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}